/**
 * CORE-1884 — one attempt to enter a Live room, seen as one thing.
 *
 * Before this, «did the person get in, and what got them in» was stitched by
 * hand from three streams that share nothing: AnalyticsEvents (page, hub),
 * RtcTelemetryEvents (RTC, its own `rtcSessionId`) and NetworkTelemetryEvents
 * (host decisions). CORE-1877 spent most of its time on exactly that and still
 * could not tell what an incognito window reset.
 *
 * An attempt starts when the room screen opens a room and ends when it leaves.
 * While it lives, its id rides on every event of all three streams
 * (`withEntryAttempt`). At the start we record the local routing state that
 * survives a reload — the things a «clear the cache / incognito» would wipe — and
 * exactly one outcome per attempt:
 *  - `joined` — both the room hub and RTC came up (`ms` from the start);
 *  - `failed` — not both by the deadline; `stage` names the first one missing;
 *  - `abandoned` — the screen left before either.
 *
 * The decision logic is pure (`advanceEntry`); the module keeps one current
 * attempt and its deadline timer.
 */
/**
 * Where the attempt's own events go. `analytics` registers itself here (it also
 * stamps the attempt id onto its events), so this module imports nothing and the
 * two never import each other.
 */
type EntryEventSink = (eventName: string, properties: Record<string, any>) => void;
let sink: EntryEventSink | null = null;
export function setEntryEventSink(next: EntryEventSink | null): void { sink = next; }

function track(eventName: string, properties: Record<string, any>): void {
  try { sink?.(eventName, properties); } catch { /* telemetry never blocks entry */ }
}

export type EntryStage = 'hub' | 'rtc';
export type EntryOutcome = 'joined' | 'failed' | 'abandoned';

/** Generous: a proxied RTC retry alone takes ~21 s + the proxy join (CORE-1877). */
export const ENTRY_DEADLINE_MS = 45_000;

export interface EntryState {
  id: string;
  roomId: string | number | null;
  startedAt: number;
  ok: Partial<Record<EntryStage, number>>;
  failures: Partial<Record<EntryStage, number>>;
  done: boolean;
}

export interface EntryOutcomeEvent {
  entryAttemptId: string;
  roomId: string | number | null;
  outcome: EntryOutcome;
  /** The first stage that did not come up (failed / abandoned). */
  stage: EntryStage | null;
  ms: number;
  hubMs: number | null;
  rtcMs: number | null;
  hubFailures: number;
  rtcFailures: number;
}

function outcomeOf(s: EntryState, outcome: EntryOutcome, now: number): EntryOutcomeEvent {
  const missing: EntryStage | null = s.ok.hub == null ? 'hub' : s.ok.rtc == null ? 'rtc' : null;
  return {
    entryAttemptId: s.id,
    roomId: s.roomId,
    outcome,
    stage: outcome === 'joined' ? null : missing,
    ms: now - s.startedAt,
    hubMs: s.ok.hub != null ? s.ok.hub - s.startedAt : null,
    rtcMs: s.ok.rtc != null ? s.ok.rtc - s.startedAt : null,
    hubFailures: s.failures.hub ?? 0,
    rtcFailures: s.failures.rtc ?? 0,
  };
}

export type EntryInput =
  | { kind: 'ok'; stage: EntryStage; at: number }
  | { kind: 'fail'; stage: EntryStage; at: number }
  | { kind: 'deadline'; at: number }
  | { kind: 'end'; at: number };

/** Pure: the next state and, at most once per attempt, its outcome. */
export function advanceEntry(s: EntryState, input: EntryInput): { state: EntryState; outcome: EntryOutcomeEvent | null } {
  if (s.done) return { state: s, outcome: null };
  if (input.kind === 'ok') {
    if (s.ok[input.stage] != null) return { state: s, outcome: null };
    const next = { ...s, ok: { ...s.ok, [input.stage]: input.at } };
    if (next.ok.hub != null && next.ok.rtc != null) {
      return { state: { ...next, done: true }, outcome: outcomeOf(next, 'joined', input.at) };
    }
    return { state: next, outcome: null };
  }
  if (input.kind === 'fail') {
    const n = (s.failures[input.stage] ?? 0) + 1;
    return { state: { ...s, failures: { ...s.failures, [input.stage]: n } }, outcome: null };
  }
  const outcome: EntryOutcome = input.kind === 'deadline' ? 'failed' : 'abandoned';
  return { state: { ...s, done: true }, outcome: outcomeOf(s, outcome, input.at) };
}

// ─── The current attempt ────────────────────────────────────────────────────

let current: EntryState | null = null;
let deadline: ReturnType<typeof setTimeout> | null = null;

function genId(): string {
  const rnd = Math.random().toString(36).slice(2, 10);
  return `ea-${Date.now().toString(36)}-${rnd}`;
}

function emitOutcome(o: EntryOutcomeEvent | null): void {
  if (!o) return;
  track('live_entry_outcome', { ...o });
}

/**
 * The routing state that survives a reload, read at the start of the attempt.
 * Injected so the module stays free of the routing modules' import graph.
 */
export type EntrySnapshotReader = () => Promise<Record<string, unknown>>;

export interface BeginEntryOptions {
  readSnapshot?: EntrySnapshotReader;
  /**
   * Stages already up when the attempt starts — a session that went live while
   * the person waited in the room: the hub connected before the attempt began.
   */
  alreadyUp?: EntryStage[];
  now?: number;
}

/** CORE-1957 — the stages a screen already has up, in the `alreadyUp` form. */
export function entryStagesUp(up: { hub: boolean; rtc: boolean }): EntryStage[] {
  return [...(up.hub ? ['hub' as const] : []), ...(up.rtc ? ['rtc' as const] : [])];
}

/** Start an attempt for `roomId`; a previous one still open is ended as abandoned. */
export function beginEntryAttempt(roomId: string | number | null, opts: BeginEntryOptions = {}): string {
  const now = opts.now ?? Date.now();
  if (current && !current.done) endEntryAttempt(now);
  const id = genId();
  current = { id, roomId, startedAt: now, ok: {}, failures: {}, done: false };
  const readSnapshot = opts.readSnapshot;
  if (deadline) clearTimeout(deadline);
  deadline = setTimeout(() => {
    if (!current || current.id !== id) return;
    const r = advanceEntry(current, { kind: 'deadline', at: Date.now() });
    current = r.state;
    emitOutcome(r.outcome);
  }, ENTRY_DEADLINE_MS);
  (deadline as { unref?: () => void }).unref?.();
  const started = (snapshot: Record<string, unknown>) => {
    track('live_entry_attempt_started', { entryAttemptId: id, roomId, ...snapshot });
  };
  if (readSnapshot) {
    readSnapshot().then(started, () => started({ snapshot: 'unavailable' }));
  } else {
    started({});
  }
  for (const stage of opts.alreadyUp ?? []) noteEntryStage(stage, 'ok', now);
  return id;
}

/** A stage came up (`ok`) or failed once (`fail`) for the current attempt. */
export function noteEntryStage(stage: EntryStage, result: 'ok' | 'fail', now = Date.now()): void {
  if (!current) return;
  const r = advanceEntry(current, result === 'ok' ? { kind: 'ok', stage, at: now } : { kind: 'fail', stage, at: now });
  current = r.state;
  emitOutcome(r.outcome);
}

/** The room screen left: an attempt without an outcome is abandoned; the id stops riding on events. */
export function endEntryAttempt(now = Date.now()): void {
  if (!current) return;
  const r = advanceEntry(current, { kind: 'end', at: now });
  emitOutcome(r.outcome);
  current = null;
  if (deadline) { clearTimeout(deadline); deadline = null; }
}

/**
 * CORE-1956 — the page is leaving (`pagehide`) or being frozen (`freeze`) with
 * the attempt still open. Neither runs React cleanups or the deadline timer: a
 * restored tab Chrome hid half-way through entry was closed without either, and
 * its attempt never got an outcome. Settle it as `abandoned` now, while the last
 * analytics batch can still go out. The id keeps riding on events — a frozen
 * page may resume in the same room.
 */
export function settleEntryAttemptOnPageExit(cause: 'pagehide' | 'freeze', now = Date.now()): void {
  if (!current || current.done) return;
  const r = advanceEntry(current, { kind: 'end', at: now });
  current = r.state;
  if (r.outcome) track('live_entry_outcome', { ...r.outcome, cause });
  if (deadline) { clearTimeout(deadline); deadline = null; }
}

/** RTC events that settle the `rtc` stage, whichever provider and platform sent them. */
const RTC_OK = new Set(['room_rtc.joined']);
const RTC_FAIL = new Set(['room_rtc.join_failed', 'room_rtc.token_failed']);

/** Fed every RTC telemetry event: the `rtc` stage is read off the names the providers already emit. */
export function observeRtcEvent(eventName: string, now = Date.now()): void {
  if (RTC_OK.has(eventName)) noteEntryStage('rtc', 'ok', now);
  else if (RTC_FAIL.has(eventName)) noteEntryStage('rtc', 'fail', now);
}

/** The current attempt id, or null outside a room. */
export function currentEntryAttemptId(): string | null {
  return current?.id ?? null;
}

/** Stamp the current attempt id onto an event's properties (no-op outside a room). */
export function withEntryAttempt<T extends Record<string, any> | undefined>(properties: T): T | Record<string, any> {
  const id = current?.id;
  if (!id) return properties;
  if (properties && 'entryAttemptId' in properties) return properties;
  return { ...(properties ?? {}), entryAttemptId: id };
}

/** Test-only reset. */
export function _resetEntryAttemptForTest(): void {
  if (deadline) clearTimeout(deadline);
  deadline = null;
  current = null;
}
